import { useState, useEffect } from 'react'
import { AssessmentProvider, useAssessment } from './context/AssessmentContext'
import WelcomeScreen from './components/screens/WelcomeScreen'
import AddictionSelection from './components/screens/AddictionSelection'
import AgeCollection from './components/screens/AgeCollection'
import RecoveryGateStep from './components/screens/RecoveryGateStep'
import PrioritizationStep from './components/screens/PrioritizationStep'
import ScreenerStep from './components/screens/ScreenerStep'
import Barc10Step from './components/screens/Barc10Step'
import DASSAssessment from './components/screens/DASSAssessment'
import EmailCollection from './components/screens/EmailCollection'
import ExtendedAssessment from './components/screens/ExtendedAssessment'
import ResultsPage from './components/screens/ResultsPage'
import ContactPage from './components/screens/ContactPage'
import UpdateToast from './components/ui/UpdateToast'
import Shell from './components/ui/Shell'
import LightField from './components/ui/LightField'
import InvestorMetricsEngine from './services/InvestorMetricsEngine'
import { getProgress } from './lib/flow'

// Same origin in production; the dev server overrides it via .env.
const API_BASE = import.meta.env.VITE_API_URL ?? ''

// How bright the light field runs on each screen, and how warm (coral) it leans.
const FIELD = {
  welcome: { intensity: 1.15, warm: 0.2 },
  results: { intensity: 1.0, warm: 0.9 },
  resultsPage: { intensity: 1.0, warm: 0.9 },
  emailCollection: { intensity: 0.85, warm: 0.7 },
}

const SCREENS = {
  welcome: WelcomeScreen,
  addictionSelection: AddictionSelection,
  ageCollection: AgeCollection,
  recoveryGate: RecoveryGateStep,
  prioritization: PrioritizationStep,
  screener: ScreenerStep,
  barc10: Barc10Step,
  dassAssessment: DASSAssessment,
  emailCollection: EmailCollection,
  extendedAssessment: ExtendedAssessment,
  results: ResultsPage,
  resultsPage: ResultsPage,
}

function AppContent() {
  const { state, dispatch } = useAssessment()
  const [remote, setRemote] = useState(() => (
    new URLSearchParams(window.location.search).has('results') ? 'loading' : 'idle'
  ))

  // Results opened from the emailed link
  useEffect(() => {
    const resultsId = new URLSearchParams(window.location.search).get('results')
    if (!resultsId) return

    let cancelled = false
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/assessments/${resultsId}`)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        if (!data.success || !data.data) throw new Error('Malformed response')
        if (cancelled) return

        const a = data.data
        dispatch({ type: 'SET_AREA_RESULTS', payload: a.addictionResults || {} })
        if (a.primaryAddiction) dispatch({ type: 'SET_PRIMARY', payload: { slug: a.primaryAddiction, overridden: false } })
        dispatch({ type: 'SET_DASS_SCORES', payload: a.dassScores || {} })
        dispatch({ type: 'SET_AGE', payload: a.userAge || null })
        dispatch({ type: 'SET_RESULTS_ID', payload: resultsId })
        dispatch({ type: 'SET_SUBMITTED_EMAIL', payload: a.userEmail || null })

        if (a.addictionResults && a.dassScores) {
          const engine = new InvestorMetricsEngine(a.addictionResults, a.dassScores, a.userAge, a.profileAnswers || null)
          dispatch({ type: 'SET_INVESTOR_METRICS', payload: engine.generateAllMetrics() })
        }
        dispatch({ type: 'SET_SCREEN', payload: 'results' })
        setRemote('idle')
      } catch (error) {
        console.error('Could not load results:', error)
        if (!cancelled) setRemote('error')
      }
    }
    load()
    return () => { cancelled = true }
  }, [dispatch])

  if (window.location.pathname === '/contact') {
    return (
      <>
        <LightField intensity={1.1} warm={0.5} />
        <div className="grain" aria-hidden="true" />
        <ContactPage />
      </>
    )
  }

  if (remote === 'loading') {
    return (
      <Shell label="Loading">
        <div className="flow" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <span className="btn__spinner" style={{ color: 'var(--ink-3)' }} aria-hidden="true" />
          <p className="flow__lede flow__lede--sm">Fetching your readout</p>
        </div>
      </Shell>
    )
  }

  if (remote === 'error') {
    return (
      <Shell label="Results">
        <div className="flow">
          <div className="flow__head">
            <h1>This link didn’t open</h1>
            <p className="flow__lede">The results may have expired or the link is incomplete. You can check the email again, or start a fresh assessment.</p>
          </div>
          <div className="flow__actions">
            <button type="button" className="btn btn--block" onClick={() => { window.location.href = '/' }}>Start a new assessment</button>
          </div>
        </div>
      </Shell>
    )
  }

  const Screen = SCREENS[state.currentScreen] || WelcomeScreen
  const field = FIELD[state.currentScreen] || { intensity: 0.75, warm: 0.15 + 0.6 * getProgress(state) }

  return (
    <>
      <LightField intensity={field.intensity} warm={field.warm} />
      <div className="grain" aria-hidden="true" />
      <Screen key={state.currentScreen} />
      <UpdateToast />
    </>
  )
}

function App() {
  return (
    <AssessmentProvider>
      <AppContent />
    </AssessmentProvider>
  )
}

export default App
